import React, {Component, ReactNode} from 'react'
import ARFacilityView from './ARFacilityView'
import ARUserView from './ARUserView'
import ARPaymentView from './ARPaymentView'
import FacilityForReservation from '../../../facilities/FacilityForReservation'
import {AdminReserveLocationState} from '../AdminReserveInterfaces'
import {PaymentInformation, ReserveFacilityModelPaymentObserver} from '../../ReserveFacilityModel'
import {CASH_PAYMENT_STATUS} from '../../../../CodeMaster'
import AROptionView from './AROptionView'
import {Nullable} from '../../../../app/TypeHelpers'
import ReserveFacilityPaymentModel from '../../ReserveFacilityPaymentModel'
import ReservePaymentCalculatorFactory from '../../paymentCalculators/ReservePaymentCalculatorFactory'
import {FacilityRentalType, ReservationClass} from '../../ReservationInterfaces'
import {updatePaymentInfo} from '../AdminReserveHelper'
import styled from 'styled-components'
import EventEmitterExAdmin from '../../../shared/pageBaseComponents/EventEmitterExAdmin'
import {TableCell} from '@mui/material'
import {NavigateFunction} from 'react-router-dom'

export type AdminReserveDetailsViewProps = PropsForConfirm | PropsForComplete

interface CommonProps {
    locationState: AdminReserveLocationState
    hasOptions: boolean
}

interface PropsForConfirm extends CommonProps {
    type: 'confirm'
    condominiumCd: string
    navigate: NavigateFunction
    onChangeCashPaymentStatus: (cashPaymentStatus: CASH_PAYMENT_STATUS) => void
}

interface PropsForComplete extends CommonProps {
    type: 'complete'
}

interface Props {
    data: AdminReserveDetailsViewProps
    observer?: ReserveFacilityModelPaymentObserver
    eventEmitter?: EventEmitterExAdmin
}

interface State {
    paymentInfo: Nullable<PaymentInformation>
}

export default class ARDetailsView extends Component<Props, State>
    implements ReserveFacilityModelPaymentObserver {

    private facilityData: FacilityForReservation

    constructor(props: Readonly<Props>) {
        super(props)

        const {locationState} = this.props.data

        if (locationState.facilityData === null) {
            throw Error('施設情報がありません。画面遷移が不正です。')
        }
        this.facilityData = locationState.facilityData

        this.state = {
            paymentInfo: null,
        }
    }

    componentDidMount(): void {
        const {reservationInfo} = this.props.data.locationState
        const model = new ReserveFacilityPaymentModel(new ReservePaymentCalculatorFactory())
        model.addPaymentInformationObserver(this)
        if (this.props.observer !== undefined) {
            model.addPaymentInformationObserver(this.props.observer)
        }
        model.withHolidays(this.props.data.locationState.holidays)

        if (this.props.data.hasOptions) {
            model.withFacilityOptionSettings(this.facilityData.facilityOptions)
            model.withReservationOptions(reservationInfo.reservationOptionInfos || [])
        }
        if (reservationInfo.type === FacilityRentalType.OVERNIGHT) {
            const facility = this.facilityData.overnightFacilitySetting
            const {startDate, stayCount} = reservationInfo
            model.withOvernightFacilityData(facility)
            model.withOvernightReservationData(startDate, stayCount)
            model.withStayAvailable(true)
        } else {
            const facility = this.facilityData.durationFacilitySetting
            const {startDate, startTime, endDate, endTime} = reservationInfo
            model.withDurationFacilityData(facility)
            model.withDurationReservationData(startDate, startTime, endDate, endTime)
            model.withStayAvailable(false)
        }
    }

    onChangePayment(paymentProps: PaymentInformation): void {
        const {items} = paymentProps
        if (items.length === 0) {
            return
        }

        let update: Nullable<PaymentInformation>
        if (this.props.data.locationState.reservationInfo.classification === ReservationClass.AGENT) {
            update = updatePaymentInfo(paymentProps, this.props.data.locationState.reservationInfo)
        } else {
            update = paymentProps
        }
        this.setState({paymentInfo: update})
    }

    render(): ReactNode {
        const {classification} = this.props.data.locationState.reservationInfo
        const {paymentInfo} = this.state
        if (classification === ReservationClass.AGENT && paymentInfo === null) {
            return null
        }

        return <DetailsView
            eventEmitter={this.props.eventEmitter}
            data={this.props.data}
            paymentInfo={paymentInfo || {items: [], totalCharge: 0}}
        />
    }
}

const DetailsView: React.FC<{
    data: AdminReserveDetailsViewProps,
    paymentInfo: PaymentInformation,
    eventEmitter?: EventEmitterExAdmin,
}> = props => {
    const {paymentInfo} = props
    const {locationState} = props.data
    const {reservationInfo, facilityData} = locationState
    if (facilityData === null) {
        throw Error('施設情報がありません。画面遷移が不正です。')
    }

    const renderConfirmViews = (propsForConfirm: PropsForConfirm) => {
        return <>
            <ARFacilityView
                type={'confirm'}
                facilityData={facilityData}
                reservationInfo={reservationInfo}
                paymentInfo={paymentInfo}
                condominiumCd={propsForConfirm.condominiumCd}
                navigate={propsForConfirm.navigate}
                locationState={locationState}
                eventEmitter={props.eventEmitter}
            />
            <ARUserView
                type={'confirm'}
                reservationInfo={reservationInfo}
                condominiumCd={propsForConfirm.condominiumCd}
                navigate={propsForConfirm.navigate}
                locationState={locationState}
            />
            {
                propsForConfirm.hasOptions &&
                <AROptionView
                    type={'confirm'}
                    facilityData={facilityData}
                    reservationInfo={reservationInfo}
                    paymentInfo={paymentInfo}
                    condominiumCd={propsForConfirm.condominiumCd}
                    navigate={propsForConfirm.navigate}
                    locationState={locationState}
                />
            }
            <ARPaymentView
                type={'confirm'}
                facilityData={facilityData}
                reservationInfo={reservationInfo}
                paymentInfo={paymentInfo}
                condominiumCd={propsForConfirm.condominiumCd}
                navigate={propsForConfirm.navigate}
                locationState={locationState}
                onChange={propsForConfirm.onChangeCashPaymentStatus}
            />
        </>
    }

    const renderCompleteViews = (propsForComplete: PropsForComplete) => {
        return <>
            <ARFacilityView
                type={'complete'}
                facilityData={facilityData}
                reservationInfo={reservationInfo}
                paymentInfo={paymentInfo}
            />
            <ARUserView
                type={'complete'}
                reservationInfo={reservationInfo}
            />
            {
                propsForComplete.hasOptions &&
                <AROptionView
                    type={'complete'}
                    facilityData={facilityData}
                    reservationInfo={reservationInfo}
                    paymentInfo={paymentInfo}
                />
            }
            <ARPaymentView
                type={'complete'}
                facilityData={facilityData}
                reservationInfo={reservationInfo}
                paymentInfo={paymentInfo}
                cashPaymentStatus={propsForComplete.locationState.cashPaymentStatus}
            />
        </>
    }

    return props.data.type === 'confirm'
        ? renderConfirmViews(props.data)
        : renderCompleteViews(props.data)
}

export const FixedWidthTableRowName = styled(TableCell)`
  width: 170px;
`
